import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { employeeBasicService, type EmployeeBasic } from '@/api/domains/employee';
import { useUI } from '@/hooks/ui';
import type { SelectorOption } from '@/components/ui';


export interface UseEmployeeSelectorReturn {
  employeeId: string;
  employees: EmployeeBasic[];
  employeeOptions: SelectorOption[];
  isLoadingEmployees: boolean;
  employeeHasMore: boolean;
  handleEmployeeChange: (value: string | string[]) => void;
  handleEmployeeSearch: (search: string) => void;
  loadMoreEmployees: () => Promise<void>;
}

export function useEmployeeSelector(): UseEmployeeSelectorReturn {
  const { t: translate } = useTranslation();
  const { showNotification } = useUI();
  
  const [employeeId, setEmployeeId] = useState<string>('');
  const [employees, setEmployees] = useState<EmployeeBasic[]>([]);
  const [employeePage, setEmployeePage] = useState<number>(1);
  const [employeeHasMore, setEmployeeHasMore] = useState<boolean>(false);
  const [employeeSearchText, setEmployeeSearchText] = useState<string>('');
  const [isLoadingEmployees, setIsLoadingEmployees] = useState<boolean>(false);
  const selectedEmployeeRef = useRef<EmployeeBasic | null>(null);

  // Load employees with pagination
  const loadEmployees = useCallback(
    async (page: number, search: string, append: boolean = false) => {
      setIsLoadingEmployees(true);
      try {
        const response = await employeeBasicService.getBasic({
          page,
          pageSize: 20,
          searchText: search || null,
        });

        if (response.success && response.data) {
          setEmployees((previous) => {
            const newEmployees = response.data?.items || [];
            let result = append ? [...previous, ...newEmployees] : newEmployees;
            
            // If there's a selected employee and it's not in the new list, prepend it
            const selected = selectedEmployeeRef.current;
            if (selected && !result.some(employee => employee.id === selected.id)) {
              result = [selected, ...result];
            }
            
            return result;
          });
          setEmployeeHasMore(response.data?.hasNextPage || false);
          setEmployeePage(page);
        } else if (response.error) {
          showNotification('error', translate('common:errors.loadingData'), 5000);
        }
      } catch {
        showNotification('error', translate('common:errors.loadingData'), 5000);
      } finally {
        setIsLoadingEmployees(false);
      }
    }, [showNotification, translate]);

  // Initial load of employees
  useEffect(() => {
    void loadEmployees(1, '');
  }, [loadEmployees]);

  // Load more employees
  const loadMoreEmployees = useCallback(async () => {
    if (!isLoadingEmployees && employeeHasMore) {
      await loadEmployees(employeePage + 1, employeeSearchText, true);
    }
  }, [isLoadingEmployees, employeeHasMore, employeePage, employeeSearchText, loadEmployees]);

  // Handle employee search
  const handleEmployeeSearch = useCallback((search: string) => {
    setEmployeeSearchText(search);
    void loadEmployees(1, search);
  }, [loadEmployees]);

  // Handle employee change
  const handleEmployeeChange = useCallback((value: string | string[]) => {
    const selectedValue = Array.isArray(value) ? value[0] ?? '' : value;
    setEmployeeId(selectedValue);
    
    // Store the selected employee to preserve it in options
    if (selectedValue) {
      const employee = employees.find((emp) => String(emp.id) === selectedValue);
      if (employee) {
        selectedEmployeeRef.current = employee;
      }
    } else {
      selectedEmployeeRef.current = null;
    }
  }, [employees]);

  // Convert employees to selector options
  const employeeOptions: SelectorOption[] = useMemo(() => 
    employees.map((employee) => ({
      value: String(employee.id),
      label: employee.rut ? `${employee.fullName} (${employee.rut})` : employee.fullName,
    })),
  [employees]);

  return {
    employeeId,
    employees,
    employeeOptions,
    isLoadingEmployees,
    employeeHasMore,
    handleEmployeeChange,
    handleEmployeeSearch,
    loadMoreEmployees,
  };
}
